import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Navigate } from '@ngxs/router-plugin';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Shell, ShellState, ShellStateModel } from '../states';
import { Observable, of } from 'rxjs';
import { PROTECTED_REMOTES_ROUTES } from 'src/app/routes/protected.routes';
import { RemoteMenuType } from '../models/enums/remote-menu.enum';
import { User, UserState } from 'shared-states';

@Injectable({ providedIn: 'root' })
export class UserPermissionGuard  {

  constructor(private _store: Store) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this._store.select(ShellState.isInitialized).pipe(
      filter((isInitialized: boolean) => isInitialized),
      switchMap(() => {
        // Allow access to unauthorized and error pages
        if (state.url === '/unauthorized' || state.url === '/error' || state.url.includes('/error')) {
          return of(true);
        }
        // Check if active module is accessible
        const activeModule = PROTECTED_REMOTES_ROUTES.find((r) => r.routePath === state.url.split('/')[1]);

        if (activeModule === undefined) {
          // Active module not found, redirect to error page
          this._store.dispatch(new Shell.SetErrorAction(new Error('Shell.Common.ErrorPage.PageNotFound')));
          this._store.dispatch(new Navigate(['/error']));    
          return of(true);
        }

        if (activeModule && activeModule.isAccessible) {
          
          // Active module is accessible, check user permission
          return this._store.select(ShellState.hasPathPermission(state.url)).pipe(
            map(hasPermission => {
              if (!hasPermission) {
                // For modules with RemoteMenuType.None, allow access without specific path permission
                if (activeModule.menuType === RemoteMenuType.None) {
                  const organizations = this._store.selectSnapshot(UserState.getOrganizations);
                  const hasAccess = organizations.some(org => org.permissions.modules.includes(activeModule.module!));
                  if (!hasAccess) {
                    this._store.dispatch(new Navigate(['/unauthorized']));
                  }
                  
                  return true;
                }
                // User doesn't have permission
                this._store.dispatch(new Navigate(['/unauthorized']));
                return false;
              }
              return true;
            }));
        } else {
          // Active module is not found or is not accessible
          this._store.dispatch(new Navigate(['/error']));
          return of(false);
        }
      })
    );
  }
}
